import { Handle, Position, type NodeProps } from '@xyflow/react';
import './LineRow.css';

/**
 * BESTELLUNG (sales_agreement) tile rendered at the top of the
 * leftmost column in the Gutschrift-Check canvas (#784 / #915 —
 * collapsed into the LS column, stacked above LS). Header-only — no
 * per-line subtree because the agreement-line data isn't part of the
 * v1 column scope.
 *
 * Two target handles for the two possible source columns:
 *   - ``title-target`` (right edge) — GS→Bestellung arrow when the
 *     GS cites the agreement via ``agreement_id`` and no LS carries
 *     a matching ``bestell_number``. GS lives at COL_RIGHT, so the
 *     dotted arrow approaches from the right.
 *   - ``title-target-left`` (left edge) — LS→Bestellung arrow when
 *     an LS card cites this agreement via ``bestell_number``. Post-
 *     #915 LS sits BELOW Bestellung in the same x column, possibly
 *     with multiple LS or Bestellung tiles between them. Smoothstep
 *     routes Left→Left handles around the LEFT side of the column
 *     (at x ≈ COL_LEFT - offset), avoiding all intervening tiles for
 *     any stack depth (codex round 4 P2).
 */
export interface BestellungNodeData {
  entityId: string;
  agreementNumber: string | null;
  name: string | null;
  supplierName: string | null;
  buyerName: string | null;
  // #1812 (FE) — SINGULAR order-doc provenance (subtask 07 / PR #1818).
  // Non-null ⇒ this tile opens the customer order in the
  // DocumentPreviewPanel on click; null ⇒ the tile is second-class
  // (default cursor, no preview). See ``useMatchGroups.BestellungSummary``.
  sourceDocId?: string | null;
  // #844 — keyboard-focus ring (see LineRowNodeData.isFocused).
  isFocused?: boolean;
  [key: string]: unknown;
}

export function BestellungNode({ data }: NodeProps) {
  const d = data as BestellungNodeData;
  const titleText = d.agreementNumber
    ? `Bestellung #${d.agreementNumber}`
    : d.name ?? 'Bestellung';
  // Org line: prefer supplier (the seller / contract counterparty) but
  // fall back to buyer when supplier is missing — matches the LS/GS
  // tile convention of always showing the relevant party.
  const orgText = d.supplierName ?? d.buyerName ?? null;
  // #1812 Part B — the tile is only a "document" (clickable → preview)
  // when the row carries the singular order-doc provenance. Without it
  // (stub-minted / pre-back-fill agreements) drop the pointer cursor so
  // the affordance doesn't promise a preview the click gate won't open.
  const hasPreview = d.sourceDocId != null;
  // Stable per-entity selector for the canvas to find this tile. The
  // per-GROUP selector (``match-group-tile-<group_id>``) is owned by
  // LineRow's primary tile; emitting it here too would double-count
  // groups in any canary that uses ``[data-testid^="match-group-tile-"]``
  // (codex round 1 P2).
  return (
    <div
      className={`line-row-node line-row-node--bestellung${hasPreview ? '' : ' line-row-node--no-preview'}${d.isFocused ? ' line-row-node--focused' : ''}`}
      data-testid={`bestellung-tile-${d.entityId}`}
    >
      <Handle id="title-target" type="target" position={Position.Right} />
      <Handle id="title-target-left" type="target" position={Position.Left} />
      <div className="line-row-node__header">
        <span className="line-row-node__doc-number">{titleText}</span>
        {/* #832 — only surface ``d.name`` in the date slot when it carries
         *  signal beyond the BestellNr already on the header. Pre-fix,
         *  agreements whose ``name`` was hydrated from the BestellNr
         *  itself (no descriptive label extracted) rendered the same
         *  identifier twice on one tile ("Bestellung #1273425 ...
         *  1273425"). The sparse-no-parties path now routes through
         *  MissingDocNode upstream (MatchGraph.tsx Bestellung column),
         *  so this guard catches the residual case where parties were
         *  extracted but the name still echoes the BestellNr. */}
        {d.name && d.agreementNumber && d.name !== d.agreementNumber && (
          <span className="line-row-node__date">{d.name}</span>
        )}
      </div>
      {orgText && (
        <div className="line-row-node__orgs">
          <span className="line-row-node__supplier">{orgText}</span>
        </div>
      )}
    </div>
  );
}
